import { useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Key, Check, ArrowRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function Redeem() {
  const { user, loading, refreshUser } = useAuth();
  const nav = useNavigate();
  const [licenseKey, setLicenseKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);

  if (loading) return <div className="p-20 text-center text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/redeem" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await http.post("/redeem", { license_key: licenseKey.trim() });
      await refreshUser();
      setSuccess(data.tier);
      toast.success(`${data.tier.toUpperCase()} tier unlocked.`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Failed to verify license");
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-6 py-16" data-testid="redeem-success">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00FFC2]/15 mb-6">
            <Check size={28} className="text-[#00FFC2]" />
          </div>
          <h1 className="text-4xl font-black tracking-tight font-display-ai">
            You're in. <span className="text-[#00FFC2]">{success.toUpperCase()}</span> unlocked.
          </h1>
          <p className="text-gray-400 mt-5 leading-relaxed">
            Unlimited Prompt Lab, all 7 modules, and {success !== "starter" ? "the Custom GPT" : "your Starter toolkit"}{" "}
            are now active on your account.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => nav("/dashboard")} className="btn-mint rounded-full px-6 py-3 text-sm inline-flex items-center gap-2" data-testid="redeem-go-dashboard">
              Open dashboard <ArrowRight size={14} />
            </button>
            <Link to="/academy/prompts" className="rounded-full px-6 py-3 text-sm border hairline hover:bg-white/5">
              Try Prompt Lab
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-6 lg:px-10 py-16" data-testid="redeem-page">
      <div className="label-eyebrow mb-3 text-center">Activate Access</div>
      <h1 className="text-4xl font-black tracking-tight font-display-ai text-center">
        Redeem your <span className="text-[#00FFC2]">license key.</span>
      </h1>
      <p className="text-gray-400 mt-4 text-center leading-relaxed">
        Already bought on Gumroad? Paste your unique license key below to unlock your tier on this
        account. You'll find it on your Gumroad receipt and in the email confirmation.
      </p>

      <form onSubmit={submit} className="mt-10 glass rounded-2xl p-7 space-y-4" data-testid="redeem-form">
        <div>
          <label className="text-xs text-gray-400 mb-1.5 block">License key</label>
          <div className="relative">
            <Key size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              required
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              data-testid="redeem-license-key"
              className="w-full bg-black/40 border hairline rounded-md pl-10 pr-4 py-3 text-sm font-mono focus:outline-none focus:border-[#00FFC2]/60"
            />
          </div>
        </div>
        {err && <div className="text-sm text-red-400" data-testid="redeem-error">{err}</div>}
        <button disabled={busy} data-testid="redeem-submit" className="w-full btn-mint py-3.5 rounded-md text-sm">
          {busy ? "Verifying…" : "Activate"}
        </button>
        <a
          href="https://gumroad.com/library"
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-xs text-gray-500 hover:text-gray-300 inline-flex items-center gap-1 justify-center pt-1"
        >
          Find my keys on Gumroad <ExternalLink size={11} />
        </a>
      </form>

      <div className="mt-8 text-xs text-gray-500 text-center">
        Don't have a license yet?{" "}
        <a href="/academy#tiers" className="text-[#00FFC2] hover:underline">View tiers</a>
      </div>
    </div>
  );
}
